// Wheel › Results: every spin this activation has recorded, what it gave away,
// and which prizes were collected (156, decision C10).
//
// READ BY WHOEVER READS PEOPLE (roleAccess.ts, `wheelResults`): the page names
// people by address, so a Viewer reads it for a show they are granted. Mark
// collected is an Admin's and a holding Editor's (`wheelCollect`), and Export
// is the export under the wheel_results purpose, so it is the export's switch
// (`wheelExport`). The service answers each of the three in its own words.
//
// A BLANK IS KEPT. It is the only evidence somebody played and the odds
// behaved, so it is listed and counted, never pruned.

import { NoticeBox, PageHead, Seg2 } from "@nova/desk-ui/parts";
import { Empty, SecLabel } from "@nova/desk-ui/kit";
import { Pager } from "@nova/desk-ui/metrics";
import { Failed, Loading } from "@nova/desk-ui/states";
import { useEffect, useState, type ReactNode } from "react";
import { readExport, type ApiOptions, type Failure } from "../api/client.js";
import { markCollected, readResults, type WheelLane, type WheelOutcome, type WheelResults as Results } from "../api/wheel.js";
import type { SessionToken } from "../session/store.js";
import { Refusal } from "./Refusal.js";
import { opens, type Reader } from "./roleAccess.js";
import { useShow } from "./Show.js";

export type WheelResultsProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onSessionEnded: (detail: string) => void;
  readonly reader: Reader;
  readonly onEnrol: () => void;
};

type Show =
  | { readonly phase: "reading" }
  | { readonly phase: "ready"; readonly results: Results }
  | { readonly phase: "failed"; readonly failure: Failure };

const LANE_LABEL: Readonly<Record<WheelLane, string>> = { either: "Either", onsite: "Onsite", web: "Web" };
const OUTCOME_LABEL: Readonly<Record<WheelOutcome, string>> = { everything: "Everything", prizes: "Prizes", blanks: "Blanks" };
const LANE_OF = { Either: "either", Onsite: "onsite", Web: "web" } as const;
const OUTCOME_OF = { Everything: "everything", Prizes: "prizes", Blanks: "blanks" } as const;

const LEDE =
  "Every spin this activation has recorded, what it gave away, and which prizes were collected. One spin per person, so this is also the count of people who played.";

export function WheelResults({ api, token, activationId, onSessionEnded, reader, onEnrol }: WheelResultsProps): ReactNode {
  const frame = useShow();
  const [show, setShow] = useState<Show>({ phase: "reading" });
  const [attempt, setAttempt] = useState(0);
  const [lane, setLane] = useState<WheelLane>("either");
  const [outcome, setOutcome] = useState<WheelOutcome>("everything");
  const [per, setPer] = useState(10);
  const [page, setPage] = useState(1);
  const [collecting, setCollecting] = useState<string | null>(null);
  const [note, setNote] = useState<{ readonly kind: "good" | "bad"; readonly detail: string } | null>(null);
  const [file, setFile] = useState<{ readonly url: string; readonly name: string; readonly rows: number } | null>(null);
  const [exporting, setExporting] = useState(false);
  const canCollect = opens(reader, "wheelCollect");
  const canExport = opens(reader, "wheelExport");

  useEffect(() => {
    if (frame.status !== "ready") return;
    let cancelled = false;
    if (show.phase !== "ready") setShow({ phase: "reading" });
    void (async () => {
      const result = await readResults(api, token, activationId, { lane, outcome, limit: per, offset: (page - 1) * per });
      if (cancelled) return;
      if (!result.ok) {
        if (result.kind === "session_ended") onSessionEnded(result.detail);
        setShow({ phase: "failed", failure: result });
        return;
      }
      setShow({ phase: "ready", results: result.value });
    })();
    return () => {
      cancelled = true;
    };
    // `show` is read only to keep the table on screen while a new page loads.
  }, [api, token, activationId, onSessionEnded, frame.status, attempt, lane, outcome, per, page]);

  if (frame.status === "failed") {
    return (
      <Refusal
        onRetry={frame.reload}
        what="Could not load the wheel's results"
        failure={frame.failure}
        role={reader.role}
        onEnrol={onEnrol}
        notCounted="No spins were read, because the activation did not load."
      />
    );
  }
  if (show.phase === "reading") {
    return (
      <div className="stack">
        <PageHead title="Results" lede={LEDE} uuid={activationId} />
        <Loading rows={2} cols={4} label="Reading the spins…" />
      </div>
    );
  }
  if (show.phase === "failed") {
    return show.failure.kind === "refused" || show.failure.kind === "session_ended" ? (
      <Refusal
        onRetry={() => setAttempt((n) => n + 1)}
        what="Could not load the wheel's results"
        failure={show.failure}
        role={reader.role}
        onEnrol={onEnrol}
        notCounted="No spins were read."
      />
    ) : (
      <div className="stack">
        <PageHead title="Results" lede={LEDE} uuid={activationId} />
        <Failed
          what="The results did not come back"
          detail="Spins, prizes and collections are one read and it failed. A count of nothing would be a wrong figure, so none is shown."
          onRetry={() => setAttempt((n) => n + 1)}
        />
      </div>
    );
  }

  const { figures, spins, matched } = show.results;

  const onCollect = async (spinId: string) => {
    setCollecting(spinId);
    setNote(null);
    const result = await markCollected(api, token, { activationId, spinId });
    setCollecting(null);
    if (!result.ok) {
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      setNote({ kind: "bad", detail: result.detail });
      return;
    }
    setNote({ kind: "good", detail: "Marked collected. The code cannot be collected again." });
    setAttempt((n) => n + 1);
  };

  const onExport = async () => {
    setExporting(true);
    setNote(null);
    setFile(null);
    const result = await readExport<{ manifest?: { row_count?: number } }>(api, token, activationId, "wheel_results");
    setExporting(false);
    if (!result.ok) {
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      setNote({ kind: "bad", detail: result.detail });
      return;
    }
    if (typeof URL.createObjectURL !== "function") {
      setNote({ kind: "bad", detail: "The export was created and recorded, but this browser could not prepare the download." });
      return;
    }
    const blob = new Blob([JSON.stringify(result.value, null, 2)], { type: "application/json" });
    setFile({
      url: URL.createObjectURL(blob),
      name: `xp1-wheel-results-${activationId.replace(/[^0-9a-z-]/gi, "")}.json`,
      rows: result.value.manifest?.row_count ?? 0,
    });
  };

  return (
    <div className="stack">
      <PageHead title="Results" lede={LEDE} uuid={activationId} />

      <section>
        <SecLabel>What the wheel gave away</SecLabel>
        {figures.spins === 0 ? (
          <Empty title="Nobody has spun yet">
            <p>A spin is recorded the moment the service picks a segment. Nothing has been recorded against this activation.</p>
          </Empty>
        ) : (
          <div className="figs figs-2">
            <div className="fig">
              <span className="fig-l">Spins</span>
              <span className="fig-v">{figures.spins}</span>
              <span className="fig-s">
                {figures.onsite} onsite · {figures.web} web
              </span>
            </div>
            <div className="fig">
              <span className="fig-l">Prizes won</span>
              <span className="fig-v">{figures.prizes}</span>
              <span className="fig-s">{Math.round((figures.prizes / figures.spins) * 100)}% of spins</span>
            </div>
            <div className="fig">
              <span className="fig-l">Collected</span>
              <span className="fig-v">{figures.collected}</span>
              <span className="fig-s">of {figures.prizes} won</span>
            </div>
            <div className="fig">
              <span className="fig-l">Not collected</span>
              <span className="fig-v">{figures.collectable}</span>
              <span className="fig-s">{figures.expired > 0 ? `still good, and ${figures.expired} expired` : "still good"}</span>
            </div>
          </div>
        )}
      </section>

      <section>
        <SecLabel info="A blank is kept. It is the only evidence that somebody played and the odds behaved, so it is never pruned to make the numbers look better.">
          Every spin
        </SecLabel>
        {/* Its own wrap rather than .filters, whose narrow layout keeps two
            segmented controls side by side and clips them at phone width. */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "12px 16px", marginBottom: 14 }}>
          <div className="field" style={{ flex: "1 1 260px", minWidth: 0 }}>
            <span className="label">Lane</span>
            <Seg2
              label="Lane"
              value={LANE_LABEL[lane]}
              options={Object.values(LANE_LABEL)}
              onPick={(label) => {
                setLane(LANE_OF[label as keyof typeof LANE_OF]);
                setPage(1);
              }}
            />
          </div>
          <div className="field" style={{ flex: "1 1 260px", minWidth: 0 }}>
            <span className="label">Outcome</span>
            <Seg2
              label="Outcome"
              value={OUTCOME_LABEL[outcome]}
              options={Object.values(OUTCOME_LABEL)}
              onPick={(label) => {
                setOutcome(OUTCOME_OF[label as keyof typeof OUTCOME_OF]);
                setPage(1);
              }}
            />
          </div>
          {canExport ? (
            <div className="field">
              <button type="button" className="btn" disabled={exporting || figures.spins === 0} onClick={() => void onExport()}>
                {exporting ? "Creating…" : "Export"}
              </button>
            </div>
          ) : null}
        </div>

        {file !== null ? (
          <NoticeBox kind="good" title="The file is ready">
            <p>
              A row for each of the {file.rows} people who spun, with the claim codes, and names and addresses only where
              each person agreed. It is recorded like every export. Nova cannot reach, amend or delete it once it is
              downloaded.
            </p>
            <div className="acts acts-flush">
              <a className="btn" href={file.url} download={file.name}>
                Download {file.name}
              </a>
            </div>
          </NoticeBox>
        ) : null}
        {note === null ? null : (
          <NoticeBox kind={note.kind} title={note.kind === "good" ? "Done" : "Not done"} alert={note.kind === "bad"}>
            <p>{note.detail}</p>
          </NoticeBox>
        )}

        {spins.length === 0 ? (
          figures.spins === 0 ? (
            <Empty title="No spin is recorded">
              <p>The wheel has not been played on this activation.</p>
            </Empty>
          ) : (
            <Empty title="Nothing matches those two filters">
              <p>
                {figures.spins} spins are recorded. A blank has no code, which is what the outcome filter reads.
              </p>
            </Empty>
          )
        ) : (
          <>
            <div className="tablewrap">
              <div className="tablescroll">
                <table className="wide">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Lane</th>
                      <th>Who</th>
                      <th>Landed on</th>
                      <th>Code</th>
                      <th>Collected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spins.map((spin) => (
                      <tr key={spin.spinId}>
                        <td data-label="When">{when(spin.spunAt)}</td>
                        <td data-label="Lane">{spin.lane === null ? <span className="quiet">not recorded</span> : LANE_LABEL[spin.lane]}</td>
                        <td data-label="Who">{spin.who ?? <span className="quiet">no address on the record</span>}</td>
                        <td data-label="Landed on">
                          {spin.prize ? spin.landedOn : <span className="quiet">{spin.landedOn || "Blank"}</span>}
                        </td>
                        <td data-label="Code" className="mono">
                          {spin.claimCode ?? <span className="quiet">—</span>}
                        </td>
                        <td data-label="Collected">
                          {!spin.prize ? (
                            <span className="quiet">nothing to collect</span>
                          ) : spin.collectedAt !== null ? (
                            <span className="tag tag-good">Collected {when(spin.collectedAt)}</span>
                          ) : spin.expired ? (
                            <span className="tag tag-off">Expired</span>
                          ) : canCollect ? (
                            <button
                              type="button"
                              className="btn btn-sm"
                              disabled={collecting !== null}
                              onClick={() => void onCollect(spin.spinId)}
                            >
                              {collecting === spin.spinId ? "Marking…" : "Mark collected"}
                            </button>
                          ) : (
                            <span className="tag tag-off">Not yet</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <Pager page={page} per={per} setPer={setPer} setPage={setPage} total={matched} unit="spins" />
          </>
        )}
      </section>
    </div>
  );
}

function when(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
